import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { http, apiErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { toast } from "sonner";
import { CheckCircle2, CreditCard, Loader2 } from "lucide-react";

export default function Billing({ dark = false }) {
  const { t } = useTranslation();
  const [opt, setOpt] = useState(null);
  const [interval, setInterval] = useState("year");
  const [busy, setBusy] = useState(false);

  const load = () => {
    http.get("/billing/options").then((r) => {
      setOpt(r.data);
      const hasMonthly = (r.data.plans || []).some((p) => p.interval === "month" && p.active);
      if (!hasMonthly) setInterval("year");
    }).catch((e) => toast.error(apiErr(e)));
  };
  useEffect(() => { load(); }, []);

  const planFor = (iv) => (opt?.plans || []).find((p) => p.interval === iv);
  const selectedPlan = planFor(interval);
  const stripeOn = !!opt?.stripe?.enabled;  // bank transfer is intentionally NOT offered to customers (owner decision)

  const price = interval === "year" ? selectedPlan?.price_year : selectedPlan?.price_month;

  const [checkoutErr, setCheckoutErr] = useState("");
  const [fallbackUrl, setFallbackUrl] = useState("");
  const PENDING_KEY = "nm:pending-checkout";

  const [returned, setReturned] = useState(false);
  const timerRef = useRef(null);

  const readPending = () => {
    try {
      const saved = JSON.parse(sessionStorage.getItem(PENDING_KEY) || "null");
      if (saved?.url && Date.now() - saved.ts < 30 * 60 * 1000) return saved;
      sessionStorage.removeItem(PENDING_KEY);
    } catch { /* ignore */ }
    return null;
  };

  const restore = () => {
    // back on this page after Stripe: `left` means the navigation DID happen (browser Back / bfcache) → no error,
    // just offer to resume; without `left` the navigation was blocked → real error with manual link
    if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    setBusy(false);
    const saved = readPending();
    if (!saved) return;
    setFallbackUrl(saved.url);
    if (saved.left) { setReturned(true); setCheckoutErr(""); }
    else setCheckoutErr(t("billing.checkout_not_redirected"));
  };

  useEffect(() => {
    restore();
    const onShow = (e) => { if (e.persisted) restore(); };
    const onHide = () => {
      if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
      const saved = readPending();
      if (saved) { try { sessionStorage.setItem(PENDING_KEY, JSON.stringify({ ...saved, left: true })); } catch { /* ignore */ } }
    };
    window.addEventListener("pageshow", onShow);
    window.addEventListener("pagehide", onHide);
    return () => { window.removeEventListener("pageshow", onShow); window.removeEventListener("pagehide", onHide); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t]);

  const checkout = async () => {
    if (!selectedPlan) return;
    setBusy(true);
    setCheckoutErr("");
    setFallbackUrl("");
    setReturned(false);
    try {
      const r = await http.post("/billing/checkout", {
        plan_id: selectedPlan.id, interval, method: "card", origin_url: window.location.origin,
      });
      const url = r?.data?.checkout_url;
      if (typeof url !== "string" || !/^https:\/\//.test(url)) {
        throw new Error(t("billing.checkout_invalid_url"));
      }
      try { sessionStorage.setItem(PENDING_KEY, JSON.stringify({ url, ts: Date.now() })); } catch { /* ignore */ }
      window.location.assign(url);
      // if the browser blocks/aborts the navigation, give control back and offer a manual link
      timerRef.current = setTimeout(() => { setBusy(false); setCheckoutErr(t("billing.checkout_not_redirected")); setFallbackUrl(url); }, 8000);
    } catch (e) {
      const msg = apiErr(e);
      setCheckoutErr(msg);
      toast.error(msg, { duration: 10000 });
      setBusy(false);
    }
  };

  if (!opt) return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></div>;

  const cardBg = dark ? "bg-white/5 border-white/10 text-slate-100" : "bg-white border-slate-200";
  const muted = dark ? "text-slate-400" : "text-slate-500";
  const FEATURE_KEYS = {
    "Clienti illimitati": "landing.pf_customers", "Preventivi e PDF": "landing.pf_quotes", "Assistente AI": "landing.pf_ai",
    "WhatsApp": "landing.pf_wa", "Collaboratori": "landing.pf_team", "Tutte le funzioni del piano annuale": "billing.all_annual_features",
  };
  const tf = (f) => (FEATURE_KEYS[f] ? t(FEATURE_KEYS[f]) : f);

  // card only: SEPA/IBAN and bank transfer are intentionally NOT offered (owner decision)
  const CardInfo = () => (
    <div data-testid="pay-method-card" className={`w-full text-left rounded-xl border p-3 flex items-start gap-3 border-sky-500 ring-1 ring-sky-500 ${!stripeOn ? "opacity-40" : ""}`}>
      <CreditCard className="w-5 h-5 mt-0.5 text-sky-500" />
      <div><div className="font-semibold text-sm">{t("billing.pay_card")}</div><div className={`text-xs ${muted}`}>{t("billing.card_desc")}</div></div>
    </div>
  );

  return (
    <div className="space-y-6" data-testid="billing-page">
      {/* Plan toggle */}
      <div>
        <h2 className="font-heading text-lg font-bold mb-3">{t("billing.choose_plan")}</h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {["year", "month"].map((iv) => {
            const p = planFor(iv);
            if (!p || !p.active) return null;
            const pr = iv === "year" ? p.price_year : p.price_month;
            const active = interval === iv;
            return (
              <button key={iv} data-testid={`plan-${iv}`} onClick={() => setInterval(iv)}
                className={`rounded-2xl border p-5 text-left transition-all ${active ? "border-sky-500 ring-2 ring-sky-500/40" : dark ? "border-white/10" : "border-slate-200"} ${cardBg}`}>
                <div className="flex items-center justify-between">
                  <div className="font-heading font-bold">{iv === "year" ? t("billing.annual") : t("billing.monthly")}</div>
                  {iv === "year" && <span className="text-[10px] uppercase bg-emerald-500/15 text-emerald-500 px-2 py-0.5 rounded-full">{t("billing.best_value")}</span>}
                </div>
                <div className="text-3xl font-extrabold text-sky-500 mt-2">€{pr}<span className={`text-base font-normal ${muted}`}>{iv === "year" ? t("billing.per_year") : t("billing.per_month")}</span></div>
                <ul className={`mt-3 space-y-1 text-sm ${muted}`}>
                  {(p.features || []).map((f) => <li key={f} className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-emerald-500" /> {tf(f)}</li>)}
                </ul>
              </button>
            );
          })}
        </div>
      </div>

      {/* Payment method */}
      <div className="grid sm:grid-cols-2 gap-3" data-testid="payment-methods">
        <CardInfo />
      </div>
      {!stripeOn && (
        <p className={`text-sm ${muted}`} data-testid="billing-none-available">{t("billing.none_available")}</p>
      )}

      {/* Action area */}
      <div>
          {stripeOn ? (
            <>
              <Button data-testid="checkout-btn" onClick={checkout} disabled={busy} className="bg-sky-500 hover:bg-sky-400 text-[#0B1120] font-semibold">
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("billing.redirecting")}</> : <>{t("billing.proceed")} · €{price}</>}
              </Button>
              {returned && !checkoutErr && (
                <div role="status" data-testid="checkout-returned" className="mt-3 text-sm rounded-xl border border-sky-400/40 bg-sky-500/10 text-sky-600 px-3 py-2">
                  {t("billing.checkout_returned")}
                  {fallbackUrl && <> <a data-testid="checkout-fallback-link" href={fallbackUrl} className="underline font-semibold">{t("billing.checkout_open_link")}</a></>}
                </div>
              )}
              {checkoutErr && (
                <div role="alert" data-testid="checkout-error" className="mt-3 text-sm rounded-xl border border-red-400/40 bg-red-500/10 text-red-500 px-3 py-2">
                  {checkoutErr}
                  {fallbackUrl && <> <a data-testid="checkout-fallback-link" href={fallbackUrl} className="underline font-semibold">{t("billing.checkout_open_link")}</a></>}
                </div>
              )}
            </>
          ) : null}
      </div>

    </div>
  );
}
